import { Link } from "react-router-dom";
import { Coffee, Hotel, MapPin, ShieldCheck, Star } from "lucide-react";
import { PromoBadges, MemberTeaser } from "@/components/customer/PromoBadges";
import { idr, ratingText, stayQuery } from "@/lib/api";
import { useT, useLoc } from "@/lib/i18n";
import Img from "@/components/Img";

export const Stars = ({ n }) => (
  <span className="flex">{Array.from({ length: n }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-[#FFB700] text-[#FFB700]" />)}</span>
);

export const RatingBadge = ({ rating, reviews }) => { const t = useT(); return (
  <div className="flex items-center gap-2">
    <div className="text-right">
      <p className="text-sm font-bold text-slate-900">{t(ratingText(rating))}</p>
      <p className="text-xs text-slate-500">{reviews.toLocaleString(t("ulasan") === "reviews" ? "en-US" : "id-ID")} {t("ulasan")}</p>
    </div>
    <span className="rounded-tl-lg rounded-br-lg bg-[#5392F9] px-2 py-1 text-sm font-bold text-white">{rating.toFixed(1)}</span>
  </div>
); };

export default function HotelCard({ item, stay, index }) {
  const { property: p, offer, badges } = item;
  const loc = useLoc();
  const t = useT();
  const disc = offer ? Math.round(offer.total_discount_percent) : 0;
  const href = `/hotel/${p.id}?${stayQuery(stay)}`;
  return (
    <Link to={href} data-testid={`hotel-card-${p.id}`} style={{ animationDelay: `${index * 60}ms` }}
      className="fade-up hover-lift group grid grid-cols-1 overflow-hidden rounded-2xl border border-slate-200 bg-white sm:grid-cols-[240px_1fr_210px]">
      <div className="relative h-52 overflow-hidden sm:h-full">
        <Img src={p.images[0]} sizes="(max-width: 640px) 100vw, 240px" alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        {disc > 0 && <span className="absolute left-3 top-3 rounded bg-[#E1231A] px-2 py-0.5 text-xs font-bold text-white">-{disc}%</span>}
      </div>
      <div className="flex flex-col gap-2 p-5">
        <h3 className="font-heading text-lg font-bold text-slate-900 group-hover:text-[#5392F9]">{p.name}</h3>
        <Stars n={p.stars} />
        <p className="flex items-center gap-1 text-sm text-[#5392F9]"><MapPin className="h-4 w-4" /> {p.area}, {p.city}</p>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {loc(p, "facilities").slice(0, 4).map((f) => <span key={f} className="rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{f}</span>)}
        </div>
        {offer && <PromoBadges promos={offer.promotions} testid={`hotel-promos-${p.id}`} />}
        <div className="mt-auto flex flex-col gap-1 pt-2 text-sm font-medium text-[#00A698]">
          {badges?.breakfast && <span className="flex items-center gap-1.5"><Coffee className="h-4 w-4" /> {t("Tersedia dengan sarapan")}</span>}
          {badges?.free_cancellation && <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> {t("Pembatalan gratis")}</span>}
          {badges?.pay_at_hotel && <span className="flex items-center gap-1.5"><Hotel className="h-4 w-4" /> {t("Bisa bayar di hotel")}</span>}
        </div>
      </div>
      <div className="flex flex-row items-end justify-between gap-3 border-t border-slate-100 p-5 sm:flex-col sm:items-end sm:border-l sm:border-t-0">
        <RatingBadge rating={p.rating || 0} reviews={p.reviews_count || 0} />
        {offer ? (
          <div className="text-right">
            {disc > 0 && <p className="text-xs text-slate-400 line-through">{idr(offer.base_avg_per_night)}</p>}
            <p data-testid={`hotel-price-${p.id}`} className="whitespace-nowrap font-heading text-xl font-extrabold text-[#E1231A]">{idr(offer.avg_per_night)}</p>
            <p className="text-xs text-slate-500">{t("per kamar/malam")}</p>
            <MemberTeaser price={offer.member_avg_per_night} idr={idr} testid={`hotel-member-price-${p.id}`} />
            {offer.available <= 3 && <p className="mt-1 text-xs font-semibold text-[#FF567D]">{t("Sisa {n} kamar!", { n: offer.available })}</p>}
            <span className="mt-3 inline-block rounded-lg bg-[#5392F9] px-4 py-2 text-sm font-bold text-white transition-colors group-hover:bg-[#3B7BE6]">{t("Pilih Kamar")}</span>
          </div>
        ) : (
          <p data-testid={`hotel-soldout-${p.id}`} className="text-sm font-semibold text-[#E1231A]">{t("Habis untuk tanggal ini")}</p>
        )}
      </div>
    </Link>
  );
}
